<script setup>
import { defineEmits, defineProps, ref, reactive, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { useEnvStore } from "@dutypay/front-end-design-system";
import { interceptor } from "@dutypay/front-end-design-system";
import { useTwoWayBinding } from '@dutypay/front-end-design-system';
import { useOneTimePasswordValidation } from "@dutypay/front-end-design-system/src/validations";
import { to } from 'await-to-js';

const { t } = useI18n();
const emit = defineEmits(["update", "trigger-authenticate"]);
const props = defineProps({
  modelValue: { type: Object, required: true },
});
const loginData = useTwoWayBinding(props, emit, "modelValue");
const secretQrCode = ref('');
const secretText = ref('');
const isLoading = ref(false);
const notification = reactive({
  fetchSecret: {
    asyncError: false
  }
});

async function fetchSecret() {
  const envStore = useEnvStore();
  notification.fetchSecret.asyncError = false;
  isLoading.value = true;
  let err, qrCode;
  [err, qrCode] = await to(interceptor.post(`${ envStore.apiUrls.dutypay }/api/v0/generate/secret`, { ...loginData.value }));
  if (err) {
    isLoading.value = false;
    notification.fetchSecret.asyncError = true;
  }
  secretQrCode.value = qrCode.data.result.qr;
  secretText.value = qrCode.data.result.plaintext;
  isLoading.value = false;
}

onMounted(async () => {
  loginData.value.authenticatorOTP = "";
  await fetchSecret();
})

function setupAuthenticator() {
  loginData.value.firstTime = true;
  loginData.value.mode = "authenticator";
  emit("trigger-authenticate");
}

const validation = {
  authenticatorOTP: [
    useOneTimePasswordValidation()
  ],
}

</script>
<script>
export default {
  name: "AuthenticatorSetupForm"
};
</script>

<template>
  <div>
    <h2>{{ t("sso.pages.LoginStages.AuthenticatorSetupForm.authenticatorSetupForm.title") }}</h2>

    <DpForm
      v-model="loginData"
      @submit="setupAuthenticator"
      :showValidationNotification="false"
      :validation="validation"
      v-loading="isLoading"
    >

      <p>{{ t("sso.pages.LoginStages.AuthenticatorSetupForm.scanCode.label") }}</p>

      <DpExpand>
        <div v-show="!!secretQrCode" class="text-align-center"
             style="margin: 1em auto; width: 228px">
          <img :src="secretQrCode" height="228" width="228" alt="QR code"/>
          <DpCopyToClipboard :value="secretText">
            <span class="monospace authenticator-setup-code">{{ secretText }}</span>
          </DpCopyToClipboard>
        </div>
      </DpExpand>

      <DpNotification
        type="error"
        v-show="notification.fetchSecret.asyncError"
        :title="t('sso.pages.LoginStages.AuthenticatorSetupForm.notification.fetchSecret.asyncError.title')"
        :message="t('sso.pages.LoginStages.AuthenticatorSetupForm.notification.fetchSecret.asyncError.message')">
        <template slot="controls">
          <DpButton purpose="secondaryAction" @click="fetchSecret">{{
              t("sso.pages.LoginStages.AuthenticatorSetupForm.tryAgain.buttonLabel")
            }}
          </DpButton>
        </template>
      </DpNotification>

      <p>{{ t("sso.pages.LoginStages.AuthenticatorSetupForm.afterYouScannedCode.label") }}</p>

      <DpInput 
          v-model="loginData.authenticatorOTP"
          id="authenticatorOTP"
          autocomplete="off"
          :validation="validation.authenticatorOTP"
          :label="t('sso.pages.LoginStages.AuthenticatorSetupForm.authenticatorOTP.input.label')"
          :placeholder="t('sso.pages.LoginStages.AuthenticatorSetupForm.authenticatorOTP.input.placeholder')" 
      />

      <template #control="{ validate }">
        <DpButton
          purpose="primaryAction"
          @click="validate">
          {{ t("sso.pages.LoginStages.AuthenticatorSetupForm.confirmCode.buttonLabel") }}
        </DpButton>
      </template>
    </DpForm>
  </div>
</template>

<style scoped lang="scss">
.authenticator-setup-code {
  margin: 1em 0;
  letter-spacing: 1px;
  word-break: break-all;
}
</style>

<i18n>
{
  "ar-AE": {
    "afterYouScannedCode": {
      "label": "تنشيط وربط التطبيق المصادقة عبر إدخال وتأكيد رمز المصادقة الصالح من التطبيق."
    },
    "authenticatorSetupForm": {
      "title": "تطبيق المصادقة"
    },
    "scanCode": {
      "label": "امسح الرمز الشريطي QR أو استخدم الرمز الموجود أدناه لتوليد أكواد التوثيق في تطبيق التوثيق."
    },
    "confirmCode": {
      "buttonLabel": "تأكيد"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "خطأ",
          "message": "رمز المصادقة غير صالح"
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "كود التوثيق من تطبيق التوثيق",
        "placeholder": "أدخل رمز التحقق"
      }
    }
  },
  "ar-DZ": {
    "afterYouScannedCode": {
      "label": "تفعيل وربط تطبيق التحقق عن طريق إدخال وتأكيد كود التحقق الصالح من التطبيق."
    },
    "authenticatorSetupForm": {
      "title": "تطبيق المصادقة"
    },
    "scanCode": {
      "label": "امسح رمز الاستجابة السريعة أو استخدم الرمز الذي يوجد أدناه لإنشاء أكواد المصادقة في تطبيق المصادقة."
    },
    "confirmCode": {
      "buttonLabel": "تأكيد"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "خطأ",
          "message": "رمز التوثيق غير صالح"
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "كود التوثيق من تطبيق التوثيق",
        "placeholder": "أدخل رمز التحقق"
      }
    }
  },
  "ar-EG": {
    "afterYouScannedCode": {
      "label": "تفعيل وربط تطبيق التوثيق بواسطة إدخال وتأكيد كود توثيق صالح من التطبيق."
    },
    "authenticatorSetupForm": {
      "title": "تطبيق المصادقة"
    },
    "scanCode": {
      "label": "امسح رمز QR أو استخدم الرمز الذي تحته لتوليد أكواد التحقق في تطبيق التحقق."
    },
    "confirmCode": {
      "buttonLabel": "تأكيد"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "خطأ",
          "message": "الرمز التوثيقي غير صالح"
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "كود التوثيق من تطبيق التوثيق",
        "placeholder": "أدخل رمز التحقق"
      }
    }
  },
  "ar-LB": {
    "afterYouScannedCode": {
      "label": "تنشيط وربط تطبيق التفويض عن طريق إدخال وتأكيد كود تفويض صالح من التطبيق."
    },
    "authenticatorSetupForm": {
      "title": "تطبيق التوثيق"
    },
    "scanCode": {
      "label": "استخدم رمز الاستجابة السريعة أو استعمل الرمز الذي أدناه لإنتاج رموز التصديق في تطبيق التصديق."
    },
    "confirmCode": {
      "buttonLabel": "تأكيد"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "خطأ",
          "message": "الرمز التوثيقي غير صالح"
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "كود التوثيق من تطبيق التوثيق",
        "placeholder": "أدخل رمز التحقق"
      }
    }
  },
  "ar-MA": {
    "afterYouScannedCode": {
      "label": "تفعيل وربط التطبيق بواسطة إدخال وتأكيد رمز التوثيق الصحيح من التطبيق."
    },
    "authenticatorSetupForm": {
      "title": "تطبيق التوثيق"
    },
    "scanCode": {
      "label": "قم بمسح رمز QR أو استخدم الرمز الموجود أدناه لإنشاء أكواد التوثيق في تطبيق التوثيق."
    },
    "confirmCode": {
      "buttonLabel": "تأكيد"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "خطأ",
          "message": "كود التوثيق غير صالح"
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "كود التوثيق من تطبيق التوثيق",
        "placeholder": "ادخل رمز التأكيد"
      }
    }
  },
  "ar-SA": {
    "afterYouScannedCode": {
      "label": "تنشيط وربط تطبيق المصادقة بواسطة إدخال وتأكيد رمز المصادقة الصحيح من التطبيق."
    },
    "authenticatorSetupForm": {
      "title": "تطبيق المصادقة"
    },
    "scanCode": {
      "label": "امسح رمز الاستجابة السريعة أو استخدم الرمز الذي أسفله لتوليد أكواد المصادقة في تطبيق المصادقة."
    },
    "confirmCode": {
      "buttonLabel": "تأكيد"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "خطأ",
          "message": "رمز التحقق غير صالح"
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "كود التوثيق من تطبيق التوثيق",
        "placeholder": "أدخل رمز التحقق"
      }
    }
  },
  "ar-SY": {
    "afterYouScannedCode": {
      "label": "تنشيط وربط تطبيق المصادقة من خلال إدخال وتأكيد رمز المصادقة الصالح من التطبيق."
    },
    "authenticatorSetupForm": {
      "title": "تطبيق المصادقة"
    },
    "scanCode": {
      "label": "قراءة الرمز الشريطي QR أو استخدام الرمز الذي في الأسفل لتوليد أكواد التصديق في ابتأليف التصديق."
    },
    "confirmCode": {
      "buttonLabel": "تأكيد"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "خطأ",
          "message": "كود التوثيق غير صالح"
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "كود التوثيق من تطبيق التوثيق",
        "placeholder": "أدخل رمز التحقق"
      }
    }
  },
  "ar-TN": {
    "afterYouScannedCode": {
      "label": "تنشيط وربط تطبيق التفويض عن طريق إدخال وتأكيد كود تفويض صالح من التطبيق."
    },
    "authenticatorSetupForm": {
      "title": "تطبيق التوثيق"
    },
    "scanCode": {
      "label": "اقرأ رمز الاستجابة السريعة أو استخدم الرمز الموجود بالاسفل لتوليد أكواد التوثيق في تطبيق التوثيق."
    },
    "confirmCode": {
      "buttonLabel": "تأكيد"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "خطأ",
          "message": "كود التوثيق غير صالح"
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "كود التوثيق من تطبيق التوثيق",
        "placeholder": "أدخل رمز التحقق"
      }
    }
  },
  "bg-BG": {
    "afterYouScannedCode": {
      "label": "Активиране и свързване на автентикационното приложение чрез въвеждане и потвърждаване на валиден автентикационен код от приложението."
    },
    "authenticatorSetupForm": {
      "title": "Приложение за удостоверяване"
    },
    "scanCode": {
      "label": "Сканирайте QR кода или използвайте кода по-долу, за да генерирате кодове за удостоверяване в приложението за удостоверяване."
    },
    "confirmCode": {
      "buttonLabel": "Потвърждение"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "грешка",
          "message": "Кодът за удостоверяване е невалиден"
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "Код за удостоверяване от приложението за удостоверяване",
        "placeholder": "Въведете код за удостоверяване"
      }
    }
  },
  "bs-BA": {
    "afterYouScannedCode": {
      "label": "Aktivacija i povezivanje autentifikacijske aplikacije putem unosa i potvrđivanja valjanog autentifikacijskog koda iz aplikacije."
    },
    "authenticatorSetupForm": {
      "title": "Aplikacija za autentifikaciju"
    },
    "scanCode": {
      "label": "Skenirajte QR kod ili koristite kod ispod da generišete autentifikacione kodove u aplikaciji za autentifikaciju."
    },
    "confirmCode": {
      "buttonLabel": "Potvrdite"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "Greška",
          "message": "Kod za autentifikaciju je nevažeći"
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "Autentifikacijski kod iz autentifikacijske aplikacije",
        "placeholder": "Unesite kod za autentifikaciju"
      }
    }
  },
  "cs-CZ": {
    "afterYouScannedCode": {
      "label": "Aktivace a propojení autentizační aplikace zadáním a potvrzením platného ověřovacího kódu z aplikace."
    },
    "authenticatorSetupForm": {
      "title": "Autentizační aplikace"
    },
    "scanCode": {
      "label": "Naskenujte QR kód nebo použijte kód níže k vygenerování ověřovacích kódů v aplikaci pro ověření."
    },
    "confirmCode": {
      "buttonLabel": "Potvrdit"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "Chyba",
          "message": "Ověřovací kód je neplatný"
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "Autentizační kód z autentizační aplikace",
        "placeholder": "Zadejte ověřovací kód"
      }
    }
  },
  "da-DK": {
    "afterYouScannedCode": {
      "label": "Aktivering og tilknytning af godkendelsesprogrammet ved indtastning og bekræftelse af en gyldig godkendelseskode fra programmet."
    },
    "authenticatorSetupForm": {
      "title": "Autentificeringsapp"
    },
    "scanCode": {
      "label": "Scan QR-koden eller brug koden nedenfor til at generere godkendelseskoder i en godkendelsesapp."
    },
    "confirmCode": {
      "buttonLabel": "Bekræft"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "Fejl",
          "message": "Godkendelseskoden er ugyldig"
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "Autentificeringskode fra autentificeringsappen",
        "placeholder": "Indtast godkendelseskode"
      }
    }
  },
  "de-DE": {
    "afterYouScannedCode": {
      "label": "Aktivierung und Verknüpfung der Authentifizierungs-App durch Eingabe und Bestätigung eines gültigen Authentifizierungscodes aus der App."
    },
    "authenticatorSetupForm": {
      "title": "Authentifizierungs-App"
    },
    "scanCode": {
      "label": "QR-Code scannen oder den darunter stehenden Code verwenden, um Authentifizierungscodes in einer Authentifizierungs-App zu generieren."
    },
    "confirmCode": {
      "buttonLabel": "Bestätigen"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "Fehler",
          "message": "Der Authentifizierungscode ist ungültig."
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "Authentifizierungscode aus der Authentifizierungs-App",
        "placeholder": "Authentifizierungscode eingeben"
      }
    }
  },
  "de-AT": {
    "afterYouScannedCode": {
      "label": "Aktivierung und Verknüpfung der Authentifizierungs-App durch Eingabe und Bestätigung eines gültigen Authentifizierungscodes aus der App."
    },
    "authenticatorSetupForm": {
      "title": "Authentifizierungs-App"
    },
    "scanCode": {
      "label": "QR-Code scannen oder den darunter stehenden Code verwenden, um Authentifizierungscodes in einer Authentifizierungs-App zu generieren."
    },
    "confirmCode": {
      "buttonLabel": "Bestätigen"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "Fehler",
          "message": "Der Authentifizierungscode ist ungültig."
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "Authentifizierungscode aus der Authentifizierungs-App",
        "placeholder": "Authentifizierungscode eingeben"
      }
    }
  },
  "de-CH": {
    "afterYouScannedCode": {
      "label": "Aktivierung und Verknüpfung der Authentifizierungs-App durch Eingabe und Bestätigung eines gültigen Authentifizierungscodes aus der App."
    },
    "authenticatorSetupForm": {
      "title": "Authentifizierungs-App"
    },
    "scanCode": {
      "label": "QR-Code scannen oder den darunter stehenden Code verwenden, um Authentifizierungscodes in einer Authentifizierungs-App zu generieren."
    },
    "confirmCode": {
      "buttonLabel": "Bestätigen"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "Fehler",
          "message": "Der Authentifizierungscode ist ungültig."
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "Authentifizierungscode aus der Authentifizierungs-App",
        "placeholder": "Authentifizierungscode eingeben"
      }
    }
  },
  "de-LU": {
    "afterYouScannedCode": {
      "label": "Aktivierung und Verknüpfung der Authentifizierungs-App durch Eingabe und Bestätigung eines gültigen Authentifizierungscodes aus der App."
    },
    "authenticatorSetupForm": {
      "title": "Authentifizierungs-App"
    },
    "scanCode": {
      "label": "QR-Code scannen oder den darunter stehenden Code verwenden, um Authentifizierungscodes in einer Authentifizierungs-App zu generieren."
    },
    "confirmCode": {
      "buttonLabel": "Bestätigen"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "Fehler",
          "message": "Der Authentifizierungscode ist ungültig."
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "Authentifizierungscode aus der Authentifizierungs-App",
        "placeholder": "Authentifizierungscode eingeben"
      }
    }
  },
  "el-GR": {
    "afterYouScannedCode": {
      "label": "Ενεργοποίηση και σύνδεση της εφαρμογής επικύρωσης με την εισαγωγή και επιβεβαίωση ενός έγκυρου κωδικού επικύρωσης από την εφαρμογή."
    },
    "authenticatorSetupForm": {
      "title": "Εφαρμογή Επαλήθευσης"
    },
    "scanCode": {
      "label": "Σαρώστε τον κώδικα QR ή χρησιμοποιήστε τον κωδικό που βρίσκεται παρακάτω για να δημιουργήσετε κωδικούς επαλήθευσης σε μια εφαρμογή επαλήθευσης."
    },
    "confirmCode": {
      "buttonLabel": "Επιβεβαίωση"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "Σφάλμα",
          "message": "Ο κωδικός επαλήθευσης είναι άκυρος"
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "Κωδικός επικύρωσης από την εφαρμογή επικύρωσης",
        "placeholder": "Εισάγετε τον κωδικό επαλήθευσης"
      }
    }
  },
  "el-CY": {
    "afterYouScannedCode": {
      "label": "Ενεργοποίηση και σύνδεση της εφαρμογής πιστοποίησης με την εισαγωγή και επιβεβαίωση ενός έγκυρου κωδικού πιστοποίησης από την εφαρμογή."
    },
    "authenticatorSetupForm": {
      "title": "Εφαρμογή Επαλήθευσης"
    },
    "scanCode": {
      "label": "Σαρώστε τον κώδικα QR ή χρησιμοποιήστε τον κωδικό παρακάτω για να δημιουργήσετε κωδικούς πιστοποίησης σε μια εφαρμογή πιστοποίησης."
    },
    "confirmCode": {
      "buttonLabel": "Επιβεβαίωση"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "Σφάλμα",
          "message": "Ο κωδικός επαλήθευσης είναι άκυρος"
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "Κωδικός επικύρωσης από την εφαρμογή επικύρωσης",
        "placeholder": "Εισάγετε τον κωδικό επαλήθευσης"
      }
    }
  },
  "en-GB": {
    "afterYouScannedCode": {
      "label": "Activation and linking of the authentication app by inputting and confirming a valid authentication code from the app."
    },
    "authenticatorSetupForm": {
      "title": "Authentication App"
    },
    "scanCode": {
      "label": "Scan the QR code or use the code below to generate authentication codes in an authentication app."
    },
    "confirmCode": {
      "buttonLabel": "Confirm"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "Error",
          "message": "The authentication code is invalid."
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "Authentication Code from Authentication App",
        "placeholder": "Enter authentication code"
      }
    }
  },
  "en-IE": {
    "afterYouScannedCode": {
      "label": "Activation and linking of the authentication app by entering and confirming a valid authentication code from the app."
    },
    "authenticatorSetupForm": {
      "title": "Authentication App"
    },
    "scanCode": {
      "label": "Scan the QR code or use the code below to generate authentication codes in an authentication app."
    },
    "confirmCode": {
      "buttonLabel": "Confirm"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "Error",
          "message": "The authentication code is invalid."
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "Authentication Code from Authentication App",
        "placeholder": "Enter authentication code"
      }
    }
  },
  "en-US": {
    "afterYouScannedCode": {
      "label": "Activation and linking of the authentication app by entering and confirming a valid authentication code from the app."
    },
    "authenticatorSetupForm": {
      "title": "Authentication App"
    },
    "scanCode": {
      "label": "Scan the QR code or use the code below to generate authentication codes in an authentication app."
    },
    "confirmCode": {
      "buttonLabel": "Confirm"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "Error",
          "message": "The authentication code is invalid."
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "Authentication Code from Authentication App",
        "placeholder": "Enter authentication code"
      }
    }
  },
  "es-ES": {
    "afterYouScannedCode": {
      "label": "Activación y vinculación de la aplicación de autenticación mediante la introducción y confirmación de un código de autenticación válido de la aplicación."
    },
    "authenticatorSetupForm": {
      "title": "Aplicación de autenticación"
    },
    "scanCode": {
      "label": "Escanee el código QR o use el código de abajo para generar códigos de autenticación en una aplicación de autenticación."
    },
    "confirmCode": {
      "buttonLabel": "Confirmar"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "Error",
          "message": "El código de autenticación es inválido"
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "Código de autenticación de la aplicación de autenticación",
        "placeholder": "Ingrese el código de autenticación"
      }
    }
  },
  "et-EE": {
    "afterYouScannedCode": {
      "label": "Autentimisrakenduse aktiveerimine ja sidumine, sisestades ja kinnitades rakendusest pärit kehtiva autentimiskoodi."
    },
    "authenticatorSetupForm": {
      "title": "Autentimisrakendus"
    },
    "scanCode": {
      "label": "Skaneeri QR-koodi või kasuta allpool olevat koodi autentimiskoodide loomiseks autentimisrakenduses."
    },
    "confirmCode": {
      "buttonLabel": "Kinnita"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "Viga",
          "message": "Autentimiskood on kehtetu"
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "Autentimiskood autentimisrakendusest",
        "placeholder": "Sisestage autentimiskood"
      }
    }
  },
  "fi-FI": {
    "afterYouScannedCode": {
      "label": "Todentamissovelluksen aktivointi ja yhdistäminen syöttämällä ja vahvistamalla sovelluksesta peräisin oleva kelvollinen todennuskoodi."
    },
    "authenticatorSetupForm": {
      "title": "Todennussovellus"
    },
    "scanCode": {
      "label": "Skannaa QR-koodi tai käytä alla olevaa koodia tunnistuskoodien generoimiseksi tunnistussovelluksessa."
    },
    "confirmCode": {
      "buttonLabel": "Vahvista"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "Virhe",
          "message": "Todennuskoodi on virheellinen"
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "Autentikointikoodi autentikointisovelluksesta",
        "placeholder": "Kirjoita tunnistekoodi"
      }
    }
  },
  "fr-BE": {
    "afterYouScannedCode": {
      "label": "Activation et liaison de l'application d'authentification en saisissant et en confirmant un code d'authentification valide depuis l'application."
    },
    "authenticatorSetupForm": {
      "title": "Application d'authentification"
    },
    "scanCode": {
      "label": "Scan the QR code or use the code below to generate authentication codes in an authentication app."
    },
    "confirmCode": {
      "buttonLabel": "Confirmer"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "Erreur",
          "message": "Le code d'authentification est invalide"
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "Code d'authentification de l'application d'authentification",
        "placeholder": "Entrez le code d'authentification"
      }
    }
  },
  "fr-CH": {
    "afterYouScannedCode": {
      "label": "Activation et liaison de l'application d'authentification en saisissant et en confirmant un code d'authentification valide depuis l'application."
    },
    "authenticatorSetupForm": {
      "title": "Application d'authentification"
    },
    "scanCode": {
      "label": "Scannez le code QR ou utilisez le code ci-dessous pour générer des codes d'authentification dans une application d'authentification."
    },
    "confirmCode": {
      "buttonLabel": "Confirmer"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "Erreur",
          "message": "Le code d'authentification est invalide"
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "Code d'authentification de l'application d'authentification",
        "placeholder": "Saisir le code d'authentification"
      }
    }
  },
  "fr-FR": {
    "afterYouScannedCode": {
      "label": "Activation et liaison de l'application d'authentification en saisissant et en confirmant un code d'authentification valide depuis l'application."
    },
    "authenticatorSetupForm": {
      "title": "Application d'authentification"
    },
    "scanCode": {
      "label": "Scannez le code QR ou utilisez le code ci-dessous pour générer des codes d'authentification dans une application d'authentification."
    },
    "confirmCode": {
      "buttonLabel": "Confirmer"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "Erreur",
          "message": "Le code d'authentification est invalide"
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "Code d'authentification de l'application d'authentification",
        "placeholder": "Saisir le code d'authentification"
      }
    }
  },
  "fr-LU": {
    "afterYouScannedCode": {
      "label": "Activation et liaison de l'application d'authentification en saisissant et en confirmant un code d'authentification valide de l'application."
    },
    "authenticatorSetupForm": {
      "title": "Application d'authentification"
    },
    "scanCode": {
      "label": "Veuillez scanner le QR-Code ou utiliser le code ci-dessous pour générer des codes d'authentification dans une application d'authentification."
    },
    "confirmCode": {
      "buttonLabel": "Confirmer"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "Erreur",
          "message": "Le code d'authentification est invalide"
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "Code d'authentification de l'application d'authentification",
        "placeholder": "Saisir le code d'authentification"
      }
    }
  },
  "ga-IE": {
    "afterYouScannedCode": {
      "label": "Gníomhachtú agus nascadh an aip fíordheimhnithe trí cód fíordheimhnithe bailí a iontráil agus a dhaingniú ón aip."
    },
    "authenticatorSetupForm": {
      "title": "Aip Authindála"
    },
    "scanCode": {
      "label": "Scan an cód QR nó úsáid an cód thíos chun códanna fíordheimhnithe a ghiniúint i app fíordheimhniúcháin."
    },
    "confirmCode": {
      "buttonLabel": "Deimhnigh"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "Earráid",
          "message": "Tá an cód fíordheimhnithe neamhbhailí"
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "Cód Fíordheimhnithe ón gCleas Fíordheimhnithe",
        "placeholder": "Cuir isteach cód fíordheimhnithe"
      }
    }
  },
  "he-IL": {
    "afterYouScannedCode": {
      "label": "הפעלה וחיבור של אפליקציית האימות על ידי הכנסת ואישור של קוד אימות תקף מהאפליקציה."
    },
    "authenticatorSetupForm": {
      "title": "אפליקציית אימות"
    },
    "scanCode": {
      "label": "סרוק את קוד ה- QR או השתמש בקוד שלהלן כדי ליצור קודי אימות באפליקציה לאימות."
    },
    "confirmCode": {
      "buttonLabel": "לְאַשֵׁר"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "שְׁגִיאָה",
          "message": "קוד האימות אינו חוקי"
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "קוד אימות מאפליקציית האימות",
        "placeholder": "הזן קוד אימות"
      }
    }
  },
  "hi-IN": {
    "afterYouScannedCode": {
      "label": "ऐप से मान्य प्रमाणीकरण कोड दर्ज करके और पुष्टि करके प्रमाणीकरण ऐप को सक्रिय करें और जोड़ें."
    },
    "authenticatorSetupForm": {
      "title": "प्रमाणीकरण ऐप"
    },
    "scanCode": {
      "label": "क्यूआर कोड स्कैन करें या नीचे दिए गए कोड का उपयोग करके एक प्रमाणीकरण ऐप में प्रमाणीकरण कोड उत्पन्न करें।"
    },
    "confirmCode": {
      "buttonLabel": "पुष्टि करें"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "त्रुटि",
          "message": "प्रमाणीकरण कोड अमान्य है"
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "प्रमाणीकरण ऐप से प्रमाणीकरण कोड",
        "placeholder": "प्रमाणीकरण कोड दर्ज करें"
      }
    }
  },
  "hr-HR": {
    "afterYouScannedCode": {
      "label": "Aktivacija i povezivanje aplikacije za autentikaciju unosom i potvrdom valjanog autentikacijskog koda iz aplikacije."
    },
    "authenticatorSetupForm": {
      "title": "Aplikacija za autentifikaciju"
    },
    "scanCode": {
      "label": "Skenirajte QR kod ili upotrijebite kod ispod za generiranje autentifikacijskih kodova u aplikaciji za autentifikaciju."
    },
    "confirmCode": {
      "buttonLabel": "Potvrda"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "Pogreška",
          "message": "Kod za autentifikaciju je nevažeći"
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "Autentifikacijski kod iz autentifikacijske aplikacije",
        "placeholder": "Unesite autentifikacijski kod"
      }
    }
  },
  "hu-HU": {
    "afterYouScannedCode": {
      "label": "Az hitelesítő alkalmazás aktiválása és összekapcsolása egy érvényes hitelesítő kód megadásával és megerősítésével az alkalmazásból."
    },
    "authenticatorSetupForm": {
      "title": "Hitelesítő alkalmazás"
    },
    "scanCode": {
      "label": "Olvassa be a QR-kódot vagy használja az alábbi kódot a hitelesítési kódok generálásához a hitelesítési alkalmazásban."
    },
    "confirmCode": {
      "buttonLabel": "Megerősít"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "Hiba",
          "message": "Az hitelesítési kód érvénytelen"
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "Hitelesítő kód a hitelesítő alkalmazásból",
        "placeholder": "Adja meg az hitelesítő kódot"
      }
    }
  },
  "it-CH": {
    "afterYouScannedCode": {
      "label": "Attivazione e collegamento dell'app di autenticazione inserendo e confermando un codice di autenticazione valido dall'app."
    },
    "authenticatorSetupForm": {
      "title": "App di autenticazione"
    },
    "scanCode": {
      "label": "Scansiona il codice QR o utilizza il codice sottostante per generare codici di autenticazione in un'app di autenticazione."
    },
    "confirmCode": {
      "buttonLabel": "Confermare"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "Errore",
          "message": "Il codice di autenticazione non è valido"
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "Codice di autenticazione dall'app di autenticazione",
        "placeholder": "Inserire il codice di autenticazione"
      }
    }
  },
  "it-IT": {
    "afterYouScannedCode": {
      "label": "Attivazione e collegamento dell'app di autenticazione inserendo e confermando un codice di autenticazione valido dall'app."
    },
    "authenticatorSetupForm": {
      "title": "App di autenticazione"
    },
    "scanCode": {
      "label": "Scansiona il codice QR o usa il codice qui sotto per generare codici di autenticazione in un'app di autenticazione."
    },
    "confirmCode": {
      "buttonLabel": "Conferma"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "Errore",
          "message": "Il codice di autenticazione non è valido"
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "Codice di autenticazione dall'app di autenticazione",
        "placeholder": "Inserire il codice di autenticazione"
      }
    }
  },
  "ja-JP": {
    "afterYouScannedCode": {
      "label": "アプリから有効な認証コードを入力し、確認して認証アプリを有効化し、リンクします。"
    },
    "authenticatorSetupForm": {
      "title": "認証アプリ"
    },
    "scanCode": {
      "label": "QRコードをスキャンするか、以下のコードを使用して認証アプリで認証コードを生成します。"
    },
    "confirmCode": {
      "buttonLabel": "確認"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "エラー",
          "message": "認証コードが無効です"
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "認証アプリからの認証コード",
        "placeholder": "認証コードを入力してください"
      }
    }
  },
  "ko-KR": {
    "afterYouScannedCode": {
      "label": "앱에서 유효한 인증 코드를 입력하고 확인하여 인증 앱을 활성화하고 연결합니다."
    },
    "authenticatorSetupForm": {
      "title": "인증 앱"
    },
    "scanCode": {
      "label": "QR 코드를 스캔하거나 아래 코드를 사용하여 인증 앱에서 인증 코드를 생성합니다."
    },
    "confirmCode": {
      "buttonLabel": "확인"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "오류",
          "message": "인증 코드가 유효하지 않습니다"
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "인증 앱에서의 인증 코드",
        "placeholder": "인증 코드를 입력하세요"
      }
    }
  },
  "lt-LT": {
    "afterYouScannedCode": {
      "label": "Autentifikavimo programos aktyvinimas ir susiejimas įvedant ir patvirtinant galiojantį autentifikavimo kodą iš programos."
    },
    "authenticatorSetupForm": {
      "title": "Autentifikavimo programa"
    },
    "scanCode": {
      "label": "Nuskanuokite QR kodą arba naudokite žemiau esantį kodą, kad sugeneruotumėte autentifikacijos kodus autentifikacijos programe."
    },
    "confirmCode": {
      "buttonLabel": "Patvirtinti"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "Klaida",
          "message": "Autentifikacijos kodas netinkamas"
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "Autentifikacijos kodas iš autentifikacijos programos",
        "placeholder": "Įveskite autentifikacijos kodą"
      }
    }
  },
  "lv-LV": {
    "afterYouScannedCode": {
      "label": "Autentifikācijas lietojumprogrammas aktivizēšana un saistīšana, ievadot un apstiprinot derīgu autentifikācijas kodu no lietojumprogrammas."
    },
    "authenticatorSetupForm": {
      "title": "Autentifikācijas lietotne"
    },
    "scanCode": {
      "label": "Noskanējiet QR kodu vai izmantojiet zemāk esošo kodu, lai ģenerētu autentifikācijas kodus autentifikācijas lietotnē."
    },
    "confirmCode": {
      "buttonLabel": "Apstiprināt"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "Kļūda",
          "message": "Autentifikācijas kods ir nederīgs"
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "Autentifikācijas kods no autentifikācijas lietotnes",
        "placeholder": "Ievadiet autentifikācijas kodu"
      }
    }
  },
  "mt-MT": {
    "afterYouScannedCode": {
      "label": "Attivazzjoni u ħolqien tal-app ta' awtentikazzjoni billi tieħu u tiċċonferma kodiċi ta' awtentikazzjoni validu mill-app."
    },
    "authenticatorSetupForm": {
      "title": "App ta' awtentikazzjoni"
    },
    "scanCode": {
      "label": "Iskanja l-kodiċi QR jew uża l-kodiċi hawn taħt biex tiġġenera kodiċi ta' awtentikazzjoni f'app ta' awtentikar."
    },
    "confirmCode": {
      "buttonLabel": "Ikkonferma"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "Żball",
          "message": "Il-kodiċi ta' awtentiċità mhux validu"
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "Kodiċi ta' awtentiċazzjoni mill-app ta' awtentiċazzjoni",
        "placeholder": "Idħol il-kodiċi ta' awtentikazzjoni"
      }
    }
  },
  "nb-NO": {
    "afterYouScannedCode": {
      "label": "Aktivering og lenking av autentiseringsapp ved å skrive inn og bekrefte en gyldig autentiseringskode fra appen."
    },
    "authenticatorSetupForm": {
      "title": "Autentiseringsapp"
    },
    "scanCode": {
      "label": "Skan QR-koden eller bruk koden nedenfor for å generere autentiseringskoder i en autentiseringsapp."
    },
    "confirmCode": {
      "buttonLabel": "Bekrefte"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "Feil",
          "message": "Autentiseringskoden er ugyldig"
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "Autentiseringskode fra autentiseringsappen",
        "placeholder": "Skriv inn godkjenningskode"
      }
    }
  },
  "nl-BE": {
    "afterYouScannedCode": {
      "label": "Activering en koppeling van de authenticatie-app door het invoeren en bevestigen van een geldige authenticatiecode van de app."
    },
    "authenticatorSetupForm": {
      "title": "Authenticatie-app"
    },
    "scanCode": {
      "label": "Scan de QR-code of gebruik de code hieronder om verificatiecodes aan te maken in een verificatie-app."
    },
    "confirmCode": {
      "buttonLabel": "Bevestigen"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "Fout",
          "message": "De verificatiecode is ongeldig"
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "Authenticatiecode uit de authenticatie-app",
        "placeholder": "Voer de authenticatiecode in"
      }
    }
  },
  "nl-NL": {
    "afterYouScannedCode": {
      "label": "Activering en koppeling van de authenticatie-app door het invoeren en bevestigen van een geldige authenticatiecode van de app."
    },
    "authenticatorSetupForm": {
      "title": "Authenticatie-app"
    },
    "scanCode": {
      "label": "Scan de QR-code of gebruik de onderstaande code om verificatiecodes te genereren in een verificatie-app."
    },
    "confirmCode": {
      "buttonLabel": "Bevestigen"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "Fout",
          "message": "De verificatiecode is ongeldig"
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "Authenticatiecode uit de authenticatie-app",
        "placeholder": "Voer de authenticatiecode in"
      }
    }
  },
  "pl-PL": {
    "afterYouScannedCode": {
      "label": "Aktywacja i połączenie aplikacji uwierzytelniającej poprzez wprowadzenie i potwierdzenie prawidłowego kodu uwierzytelniającego z aplikacji."
    },
    "authenticatorSetupForm": {
      "title": "Aplikacja do uwierzytelniania"
    },
    "scanCode": {
      "label": "Zeskanuj kod QR lub użyj kodu znajdującego się poniżej, aby wygenerować kody uwierzytelniania w aplikacji uwierzytelniania."
    },
    "confirmCode": {
      "buttonLabel": "Potwierdź"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "Błąd",
          "message": "Kod uwierzytelniania jest nieprawidłowy"
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "Kod uwierzytelniania z aplikacji uwierzytelniania",
        "placeholder": "Wprowadź kod uwierzytelniania"
      }
    }
  },
  "pt-PT": {
    "afterYouScannedCode": {
      "label": "Ativação e associação do aplicativo de autenticação ao inserir e confirmar um código de autenticação válido do aplicativo."
    },
    "authenticatorSetupForm": {
      "title": "Aplicativo de autenticação"
    },
    "scanCode": {
      "label": "Digitalize o código QR ou use o código abaixo para gerar códigos de autenticação em um aplicativo de autenticação."
    },
    "confirmCode": {
      "buttonLabel": "Confirmar"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "Erro",
          "message": "O código de autenticação é inválido"
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "Código de autenticação do aplicativo de autenticação",
        "placeholder": "Insira o código de autenticação"
      }
    }
  },
  "ro-RO": {
    "afterYouScannedCode": {
      "label": "Activarea și conectarea aplicației de autentificare prin introducerea și confirmarea unui cod de autentificare valid din aplicație."
    },
    "authenticatorSetupForm": {
      "title": "Aplicație de autentificare"
    },
    "scanCode": {
      "label": "Scanați codul QR sau utilizați codul de mai jos pentru a genera coduri de autentificare într-o aplicație de autentificare."
    },
    "confirmCode": {
      "buttonLabel": "Confirmare"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "Eroare",
          "message": "Codul de autentificare este invalid"
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "Codul de autentificare din aplicația de autentificare",
        "placeholder": "Introduceți codul de autentificare"
      }
    }
  },
  "ru-RU": {
    "afterYouScannedCode": {
      "label": "Активация и привязка приложения аутентификации путем ввода и подтверждения действительного кода аутентификации из приложения."
    },
    "authenticatorSetupForm": {
      "title": "Приложение для аутентификации"
    },
    "scanCode": {
      "label": "Сканируйте QR-код или используйте код ниже, чтобы генерировать коды аутентификации в приложении аутентификации."
    },
    "confirmCode": {
      "buttonLabel": "Подтвердить"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "Ошибка",
          "message": "Код аутентификации недействителен"
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "Код аутентификации из приложения аутентификации",
        "placeholder": "Введите код аутентификации"
      }
    }
  },
  "sk-SK": {
    "afterYouScannedCode": {
      "label": "Aktivácia a prepojenie autentifikačnej aplikácie zadaním a potvrdením platného autentifikačného kódu z aplikácie."
    },
    "authenticatorSetupForm": {
      "title": "Autentifikačná aplikácia"
    },
    "scanCode": {
      "label": "Naskenujte QR kód alebo použite kód nižšie na generovanie overovacích kódov v overovacej aplikácii."
    },
    "confirmCode": {
      "buttonLabel": "Potvrdiť"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "Chyba",
          "message": "Autentifikačný kód je neplatný"
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "Autentizačný kód z autentizačnej aplikácie",
        "placeholder": "Zadajte overovací kód"
      }
    }
  },
  "sl-SI": {
    "afterYouScannedCode": {
      "label": "Aktivacija in povezovanje aplikacije za avtentikacijo z vnosom in potrditvijo veljavne avtentikacijske kode iz aplikacije."
    },
    "authenticatorSetupForm": {
      "title": "Aplikacija za avtentikacijo"
    },
    "scanCode": {
      "label": "Skenirajte QR kodo ali uporabite kodo spodaj za generiranje overitvenih kod v aplikaciji za overjanje."
    },
    "confirmCode": {
      "buttonLabel": "Potrdi"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "Napaka",
          "message": "Koda za overitev je neveljavna"
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "Avtentikacijska koda iz avtentikacijske aplikacije",
        "placeholder": "Vnesite kodo za preverjanje pristnosti"
      }
    }
  },
  "sv-SE": {
    "afterYouScannedCode": {
      "label": "Aktivering och länkning av autentiseringsappen genom att ange och bekräfta en giltig autentiseringskod från appen."
    },
    "authenticatorSetupForm": {
      "title": "Autentiseringsapp"
    },
    "scanCode": {
      "label": "Skanna QR-koden eller använd koden nedan för att generera autentiseringskoder i en autentiseringsapp."
    },
    "confirmCode": {
      "buttonLabel": "Bekräfta"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "Fel",
          "message": "Autentiseringskoden är ogiltig"
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "Autentiseringskod från autentiseringsappen",
        "placeholder": "Ange autentiseringskod"
      }
    }
  },
  "th-TH": {
    "afterYouScannedCode": {
      "label": "การเปิดใช้งานและการเชื่อมโยงแอพพลิเคชันการยืนยันความถูกต้องโดยการป้อนและยืนยันรหัสการยืนยันความถูกต้องจากแอพพลิเคชัน"
    },
    "authenticatorSetupForm": {
      "title": "แอปพลิเคชันการรับรองความถูกต้อง"
    },
    "scanCode": {
      "label": "สแกนรหัส QR หรือใช้รหัสด้านล่างเพื่อสร้างรหัสยืนยันในแอปพลิเคชั่นการยืนยัน"
    },
    "confirmCode": {
      "buttonLabel": "ยืนยัน"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "ความผิดพลาด",
          "message": "รหัสการตรวจสอบไม่ถูกต้อง"
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "รหัสการรับรองจากแอปการรับรองความถูกต้อง",
        "placeholder": "ป้อนรหัสยืนยัน"
      }
    }
  },
  "tr-CY": {
    "afterYouScannedCode": {
      "label": "Uygulamadan geçerli bir kimlik doğrulama kodu girilerek ve onaylanarak kimlik doğrulama uygulamasının etkinleştirilmesi ve bağlanması."
    },
    "authenticatorSetupForm": {
      "title": "Kimlik Doğrulama Uygulaması"
    },
    "scanCode": {
      "label": "QR kodunu tarayın veya aşağıdaki kodu kullanarak bir kimlik doğrulama uygulamasında kimlik doğrulama kodları oluşturun."
    },
    "confirmCode": {
      "buttonLabel": "Onayla"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "Hata",
          "message": "Doğrulama kodu geçersiz"
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "Doğrulama Uygulamasından Doğrulama Kodu",
        "placeholder": "Doğrulama kodunu girin"
      }
    }
  },
  "tr-TR": {
    "afterYouScannedCode": {
      "label": "Uygulamadan geçerli bir kimlik doğrulama kodu girerek ve onaylayarak kimlik doğrulama uygulamasının etkinleştirilmesi ve bağlantı kurulması."
    },
    "authenticatorSetupForm": {
      "title": "Kimlik Doğrulama Uygulaması"
    },
    "scanCode": {
      "label": "QR kodunu tarayın veya aşağıdaki kodu kullanarak bir kimlik doğrulama uygulamasında kimlik doğrulama kodları oluşturun."
    },
    "confirmCode": {
      "buttonLabel": "Onayla"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "Hata",
          "message": "Doğrulama kodu geçersiz"
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "Doğrulama Uygulamasından Doğrulama Kodu",
        "placeholder": "Doğrulama kodunu girin"
      }
    }
  },
  "uk-UA": {
    "afterYouScannedCode": {
      "label": "Активація та зв'язування програми аутентифікації шляхом введення та підтвердження дійсного коду аутентифікації з програми."
    },
    "authenticatorSetupForm": {
      "title": "Додаток для автентифікації"
    },
    "scanCode": {
      "label": "Скануйте QR-код або використовуйте код нижче для генерації кодів автентифікації в додатку для автентифікації."
    },
    "confirmCode": {
      "buttonLabel": "Підтвердити"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "Помилка",
          "message": "Код автентифікації недійсний"
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "Код автентифікації з додатка автентифікації",
        "placeholder": "Введіть код автентифікації"
      }
    }
  },
  "vi-VN": {
    "afterYouScannedCode": {
      "label": "Kích hoạt và liên kết ứng dụng xác thực bằng cách nhập và xác nhận mã xác thực hợp lệ từ ứng dụng."
    },
    "authenticatorSetupForm": {
      "title": "Ứng dụng Xác thực"
    },
    "scanCode": {
      "label": "Quét mã QR hoặc sử dụng mã dưới đây để tạo mã xác thực trong ứng dụng xác thực."
    },
    "confirmCode": {
      "buttonLabel": "Xác nhận"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "Lỗi",
          "message": "Mã xác thực không hợp lệ"
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "Mã xác thực từ ứng dụng xác thực",
        "placeholder": "Nhập mã xác thực"
      }
    }
  },
  "zh-CN": {
    "afterYouScannedCode": {
      "label": "通过从应用程序中输入和确认有效的认证代码，激活和连接认证应用程序."
    },
    "authenticatorSetupForm": {
      "title": "认证应用"
    },
    "scanCode": {
      "label": "扫描QR码或使用下面的代码在认证应用中生成认证代码。"
    },
    "confirmCode": {
      "buttonLabel": "确认"
    },
    "notification": {
      "fetchSecret": {
        "asyncError": {
          "title": "错误",
          "message": "认证代码无效"
        }
      }
    },
    "authenticatorOTP": {
      "input": {
        "label": "来自身份验证应用的身份验证代码",
        "placeholder": "请输入认证码"
      }
    }
  }
}
</i18n>
